"use client";

// الإطار المشترك للصفحة الرئيسيّة وصفحة الدخول (2026-10-05): كلّ ما فوق وما تحت واحد في
// الصفحتين، والفرق ما يوضع في الوسط (`children`). المحتوى والبطاقات والتأثيرات والشعار من
// الإعدادات ← المظهر والألوان (`settings.landing`، انظر src/lib/theme/landing.ts)؛ و`config`
// تمرّر مسوّدة للمعاينة الحيّة. الألوان من الثيم وحده؛ الحركة تتوقّف مع «تقليل الحركة».
import { useEffect, useState, type ReactNode } from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/lib/theme/useTheme";
import { LANDING_ICONS, LANDING_TONES, resolveLanding, type LandingSettings } from "@/lib/theme/landing";
import { BrandLogo } from "@/lib/theme/branding";
import { useModeSwitch } from "@/lib/theme/mode";
const cn = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

const FLOATING: { icon: keyof typeof LANDING_ICONS; pos: string; delay: string }[] = [
  { icon: "tv", pos: "top-[14%] right-[8%]", delay: "0s" },
  { icon: "antenna", pos: "top-[55%] right-[4%]", delay: "-2s" },
  { icon: "facebook", pos: "top-[12%] left-[9%]", delay: "-1s" },
  { icon: "globe", pos: "top-[48%] left-[5%]", delay: "-3s" },
  { icon: "chart", pos: "top-[30%] left-[20%]", delay: "-4s" },
  { icon: "mic", pos: "top-[32%] right-[20%]", delay: "-5s" },
];

export function LandingShell({ children, title, config, preview = false }: {
  children: ReactNode;
  title?: string;
  config?: Partial<LandingSettings> | null;
  preview?: boolean;
}) {
  const { settings } = useTheme();
  const mode = useModeSwitch();
  const [mounted, setMounted] = useState(false);
  // الخادم يرسم بالافتراضيّ، والمتصفّح قد يملك إعدادات محفوظة ⇒ تُطبَّق بعد التركيب فقط
  // (كان اختلافهما خطأ hydration)، والصفحة مخفيّة حتى ذلك فلا يومض النصّ الافتراضيّ.
  const L = resolveLanding(mounted ? (config !== undefined ? config : settings?.landing) : null);
  const fx = L.effects;
  useEffect(() => {
    setMounted(true);
    if (!preview) document.title = title ? `${title} — ${L.name}` : L.name;
  }, [title, preview, L.name]);
  const dark = mounted && mode.dark;

  return (
    <main
      dir="rtl"
      className={cn("relative w-full overflow-hidden bg-th-bg text-th-text flex flex-col transition-opacity duration-200",
        preview ? "min-h-[560px]" : "min-h-dvh", !mounted && "opacity-0")}
      style={{
        backgroundImage:
          "radial-gradient(circle at 85% 0%, rgb(var(--color-primary) / 0.14) 0, transparent 55%)," +
          "radial-gradient(circle at 0% 100%, rgb(var(--color-primary) / 0.10) 0, transparent 45%)",
      }}
    >
      {fx.auras && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <span className="mc-aura absolute -top-16 -right-10 size-72 rounded-full" style={{ background: "rgb(var(--color-primary) / 0.35)" }} />
          <span className="mc-aura absolute -bottom-16 -left-10 size-60 rounded-full bg-success-500" style={{ animationDelay: "-5s", opacity: 0.22 }} />
          <span className="mc-aura absolute top-[40%] left-[40%] size-48 rounded-full bg-info-500" style={{ animationDelay: "-9s", opacity: 0.18 }} />
        </div>
      )}
      {fx.floatingIcons && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden sm:block">
          {FLOATING.map(({ icon, pos, delay }) => {
            const Icon = LANDING_ICONS[icon].Icon;
            return <Icon key={icon} className={cn("mc-bob absolute size-6 text-accent-700 opacity-30", pos)} style={{ animationDelay: delay }} />;
          })}
        </div>
      )}

      {fx.themeToggle && mode.allowed && (
        <button
          type="button"
          onClick={mode.toggle}
          className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full border border-th-border bg-th-surface px-3 py-1.5 text-xs text-th-text hover:bg-th-surface-alt"
          aria-label={dark ? "الوضع الفاتح" : "الوضع الداكن"}
        >
          {dark ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
          {mounted ? (dark ? "فاتح" : "داكن") : " "}
        </button>
      )}

      <div className="relative z-[1] mx-auto w-full max-w-4xl flex-1 px-4 pt-16 pb-8 flex flex-col items-center text-center">
        <div className="relative mb-5">
          {fx.radar && (
            <span aria-hidden="true" className="pointer-events-none absolute inset-0 grid place-items-center">
              {[0, 0.8, 1.6].map((d) => (
                <span key={d} className="mc-ping absolute size-20 rounded-full border-2 border-accent-500" style={{ animationDelay: `${d}s` }} />
              ))}
            </span>
          )}
          <BrandLogo place="main" alt={L.name} className="relative h-20 w-auto" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-th-text">{L.name}</h1>
        <p className="mt-2 text-sm md:text-base text-th-text-muted">{L.tagline}</p>

        <p className="mt-8 max-w-2xl text-lg md:text-xl font-semibold leading-relaxed text-accent-700">{L.headline}</p>
        <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-th-text-muted">{L.line1}</p>
        <p className="mt-1 max-w-2xl text-sm md:text-base leading-relaxed text-th-text-muted">{L.line2}</p>

        {fx.wave && (
          <div aria-hidden="true" className="mt-5 flex h-7 items-center gap-[3px]">
            {Array.from({ length: 24 }, (_, i) => (
              <span key={i} className="mc-eq w-1 rounded-full bg-accent-600"
                style={{ animationDelay: `${-((i * 0.13) % 1)}s`, animationDuration: `${0.7 + ((i * 37) % 5) / 10}s` }} />
            ))}
          </div>
        )}

        <div className="mt-8 w-full flex justify-center">{children}</div>

        {fx.showCards && L.cards.length > 0 && (
          <div className="mt-12 grid w-full grid-cols-1 gap-3 md:grid-cols-3">
            {L.cards.map((c, i) => {
              const Icon = (LANDING_ICONS[c.icon] ?? LANDING_ICONS.tv).Icon;
              return (
                <div key={i} className={cn("rounded-xl border border-th-border bg-th-surface p-4 text-right transition-transform duration-200",
                  fx.cardMotion && "hover:-translate-y-1 hover:shadow-md")}>
                  {fx.cardMotion ? (
                    <span className={cn("inline-flex size-9 items-center justify-center rounded-lg", (LANDING_TONES[c.tone] ?? LANDING_TONES.accent).badge)}>
                      <Icon className="size-5" aria-hidden="true" />
                    </span>
                  ) : (
                    <Icon className="size-5 text-accent-700" aria-hidden="true" />
                  )}
                  <p className="mt-2 text-sm font-semibold text-th-text">{c.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-th-text-muted">{c.text}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <footer className="relative z-[1] pb-12 pt-2 text-center text-xs text-th-text-muted">{L.footer} <bdi dir="ltr">© {new Date().getFullYear()}</bdi></footer>
    </main>
  );
}
